/**
 * Latex Component
 *
 * Renders mathematical formulas using KaTeX.
 * Detects and renders LaTeX patterns like $formula$ or $$formula$$ in text.
 *
 * Note: KaTeX and its CSS are lazy-loaded on first use to reduce initial bundle size.
 */

import { useEffect, useRef, useState, useId } from 'react';

interface LatexProps {
  formula: string;
  display?: boolean;
}

// Lazy load KaTeX module
let katexModule: typeof import('katex') | null = null;
let katexLoadPromise: Promise<typeof import('katex')> | null = null;
let cssLoaded = false;

async function loadKatexCSS() {
  if (cssLoaded) return;
  cssLoaded = true;

  // Inject KaTeX CSS dynamically
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = '/node_modules/katex/dist/katex.min.css';
  document.head.appendChild(link);
}

async function loadKatex(): Promise<typeof import('katex')> {
  if (katexModule) return katexModule;
  if (katexLoadPromise) return katexLoadPromise;

  katexLoadPromise = Promise.all([
    import('katex'),
    loadKatexCSS(),
  ]).then(([module]) => {
    katexModule = module;
    return module;
  });

  return katexLoadPromise;
}

/**
 * Render a single LaTeX formula
 */
export function Latex({ formula, display = false }: LatexProps) {
  const containerRef = useRef<HTMLSpanElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const id = useId();

  useEffect(() => {
    let isMounted = true;

    loadKatex().then((katex) => {
      if (!isMounted || !containerRef.current) return;

      try {
        katex.render(formula, containerRef.current, {
          displayMode: display,
          throwOnError: false,
          trust: true,
        });
      } catch (error) {
        // Fallback to plain text if rendering fails
        if (containerRef.current) {
          containerRef.current.textContent = formula;
        }
      }
      setIsLoaded(true);
    });

    return () => {
      isMounted = false;
    };
  }, [formula, display, id]);

  // Show formula as fallback while loading
  return <span ref={containerRef} style={{ opacity: isLoaded ? 1 : 0.7 }} />;
}

/**
 * Parse and render text with embedded LaTeX formulas
 *
 * Supports:
 * - $inline$ formulas
 * - $$display$$ formulas (on separate lines)
 */
export function LatexText({ text }: { text: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const id = useId();

  useEffect(() => {
    let isMounted = true;

    loadKatex().then((katex) => {
      if (!isMounted || !containerRef.current) return;

      // Split by $...$ patterns and render each part
      const parts: Array<{ text: string; isLatex: boolean; isDisplay?: boolean }> = [];
      let remaining = text;

      while (remaining.length > 0) {
        // Check for display math $$...$$
        const displayMatch = remaining.match(/^\$\$([\s\S]+?)\$\$/);
        if (displayMatch) {
          parts.push({ text: displayMatch[1], isLatex: true, isDisplay: true });
          remaining = remaining.slice(displayMatch[0].length);
          continue;
        }

        // Check for inline math $...$
        const inlineMatch = remaining.match(/^\$([^$\n]+?)\$/);
        if (inlineMatch) {
          parts.push({ text: inlineMatch[1], isLatex: true, isDisplay: false });
          remaining = remaining.slice(inlineMatch[0].length);
          continue;
        }

        // Find the next $ or end of string
        const nextDollar = remaining.indexOf('$');
        if (nextDollar === -1) {
          parts.push({ text: remaining, isLatex: false });
          break;
        }

        parts.push({ text: remaining.slice(0, nextDollar), isLatex: false });
        remaining = remaining.slice(nextDollar);
      }

      // Render each part
      containerRef.current.innerHTML = '';
      parts.forEach((part) => {
        if (part.isLatex) {
          const span = document.createElement('span');
          if (part.isDisplay) {
            span.style.display = 'block';
            span.style.textAlign = 'center';
            span.style.margin = '8px 0';
          }
          containerRef.current!.appendChild(span);

          try {
            katex.render(part.text, span, {
              displayMode: part.isDisplay || false,
              throwOnError: false,
              trust: true,
            });
          } catch (error) {
            span.textContent = `$${part.text}$`;
          }
        } else {
          const textNode = document.createTextNode(part.text);
          containerRef.current!.appendChild(textNode);
        }
      });

      setIsLoaded(true);
    });

    return () => {
      isMounted = false;
    };
  }, [text, id]);

  return <div ref={containerRef} style={{ opacity: isLoaded ? 1 : 0.7 }} />;
}
